import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk';
import meAPI from '@api/me';
import { useUserStore } from '@store/user';

/**
 * Intercom Messenger.
 *
 * The messenger is only ever booted for a signed-in user, and it is the API —
 * never this app — that says who that user is: `GET /me/intercom-token` returns
 * a short-lived JWT signed with the workspace secret, and every attribute
 * Intercom should trust (id, name, email, phone, created_at, company, tenant)
 * lives inside it. Attributes passed alongside the token are ignored by
 * Intercom once the workspace requires verified updates, so we deliberately
 * send nothing but `app_id` and the token itself.
 *
 * Fails closed: no token (secret unset, or the request failed) means the
 * messenger stays down rather than booting with unverified identity.
 *
 * Gated on `VITE_INTERCOM_APP_ID`, so local/CI builds without the key are a
 * no-op — same convention as `usePosthog` / `openLisaHelp`.
 */

/** `Intercom()` injects the widget script — that can only usefully happen once per page load. */
let scriptInjected = false;

/** Whether the messenger is currently booted with a user (false after a `shutdown()`). */
let booted = false;

type IntercomSettings = Record<string, unknown>;

async function fetchToken(tenantId: unknown): Promise<string | null> {
    try {
        const { data } = await meAPI.retrieveIntercomToken(tenantId);

        return data?.token ?? null;
    } catch (error) {
        console.error('Intercom: could not mint a user token, leaving the messenger down', error);

        return null;
    }
}

function identify(settings: IntercomSettings): void {
    if (!scriptInjected) {
        Intercom(settings);
        scriptInjected = true;
        booted = true;

        return;
    }

    // Re-authenticated after a logout — the widget script is loaded but shut down.
    if (!booted) {
        boot(settings);
        booted = true;

        return;
    }

    update(settings);
}

function reset(): void {
    if (!booted) {
        return;
    }

    shutdown();
    booted = false;
}

export function useIntercom() {
    const appId = import.meta.env.VITE_INTERCOM_APP_ID;

    if (!appId) {
        return;
    }

    const userStore = useUserStore();

    watch(
        () => [userStore.isAuthenticated, userStore.data?.id, userStore.tenantId] as const,
        async ([isAuthenticated, userId, tenantId], _previous, onCleanup) => {
            if (!isAuthenticated || !userId) {
                reset();

                return;
            }

            // A newer auth state can land while the token request is in flight;
            // ignore a stale response rather than identifying as the old user.
            let superseded = false;
            onCleanup(() => {
                superseded = true;
            });

            const token = await fetchToken(tenantId);

            if (superseded) {
                return;
            }

            if (!token) {
                reset();

                return;
            }

            const settings: IntercomSettings = {
                app_id: appId,
                intercom_user_jwt: token,
            };

            const region = import.meta.env.VITE_INTERCOM_REGION;

            if (region) {
                settings.region = region;
            }

            identify(settings);
        },
        { immediate: true },
    );
}
